import { Loading, SplitPanel } from '@components/atomic/atoms';
import CheckList from '@components/checklist/checkList';
import UploadDocument from '@components/form/upload/uploadDocuments';
import { IcoClose } from '@icons';
import { getModalData /*, getOeSubjectRoles*/ } from '@lib/api';
// import { dateFormatter /*, formatToDecimals*/ } from '@lib/helpers/formatting';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { /*Fragment,*/ useState } from 'react';
import { Button, /*Col, Form,*/ Modal /*, Row*/ } from 'react-bootstrap';
// import SummaryFrameworkcontractModalInvolvedSubjects from './SummaryFrameworkcontractModalInvolvedSubjects';
import { ResumeChecklistProjectType } from './ResumeChecklistCellRenderer';
// import StopLightWithMessage from './StopLightWithMessage';
import SummaryFrameworkcontractModalInfo from './SummaryFrameworkcontractModalInfo';
import SummaryDownloadList from './SummaryDownloadList';
import { useHasPermission } from '@lib/hooks/useHasPermission';
import { useCanEditChecklist } from '@lib/hooks/useCanEditChecklist';

interface SummaryFrameworkcontractModalProps {
	show: boolean;
	onHide: () => void;
	apiUrl: string;
	project: ResumeChecklistProjectType;
}

export type FrameworkcontractDataType = {
	id: number;
	number: string;
	reported: boolean;
	reporting: boolean;
	tender: {
		tenderNumber?: string;
		companyId?: number;
	};
	cigs?: {
		id: number;
		cig: string;
	}[];
	date?: string;
	lifetime?: string;
	extension?: string;
	countryCode?: string;
	vatNumber?: string;
	checkSignatures?: boolean;
	subjectRole?: string;
	checkliststatus?: {
		color: string;
	};
};

const SummaryFrameworkcontractModal: React.FC<SummaryFrameworkcontractModalProps> = ({
	show,
	onHide: onHideFromParent,
	apiUrl,
	project,
}) => {
	const { data: modalData, isPending: isPendingModalData } = useQuery<{
		data: FrameworkcontractDataType;
	}>({
		queryKey: ['modalData', apiUrl],
		queryFn: () => getModalData(apiUrl),
		enabled: show,
	});
	const frameworkcontractData = modalData?.data;

	const queryClient = useQueryClient();
	const invalidateInvoicesQuery = () => {
		queryClient.invalidateQueries({ queryKey: ['resumeInvoices'] });
	};

	const [isEdited, setIsEdited] = useState(false);
	const onHide = () => {
		if (isEdited) {
			invalidateInvoicesQuery();
		}
		setIsEdited(false);
		onHideFromParent();
	};

	const canEditChecklist = useCanEditChecklist('frameworkcontracts');
	const isDisabled = frameworkcontractData?.reported || frameworkcontractData?.reporting || !canEditChecklist;

	const shouldShowChecklist = useHasPermission('view-checklists');

	return (
		<Modal fullscreen show={show} onHide={onHide}>
			<Modal.Header
				style={{
					padding: '2rem',
					justifyContent: 'space-between',
				}}
			>
				<Modal.Title>
					<h2 style={{ fontSize: '28px', marginBottom: '0.5rem' }}>Controllo del contratto aperto {frameworkcontractData?.id}</h2>
					<h3
						style={{
							fontSize: '18px',
							fontWeight: 700,
						}}
					>
						PROGETTO: {project.name}
					</h3>
				</Modal.Title>
				<Button
					className="btn custom-ghost-button"
					onClick={onHide}
					style={{
						color: 'var(--np-primary-color)',
						display: 'flex',
						alignItems: 'center',
						gap: '0.75rem',
					}}
				>
					Chiudi
					<IcoClose
						style={{
							fill: 'var(--np-primary-color)',
						}}
					/>
				</Button>
			</Modal.Header>
			<Modal.Body
				style={{
					padding: '2rem',
					overflow: 'hidden',
				}}
			>
				{isPendingModalData ? (
					<Loading />
				) : show ? (
					<SplitPanel
						panels={[
							{
								key: 'info',
								title: 'Informazioni',
								content: <SummaryFrameworkcontractModalInfo frameworkcontractData={frameworkcontractData} />,
							},

							{
								key: 'documents',
								title: 'Documenti',
								content: (
									<div>
										<UploadDocument entity="frameworkcontracts" entityId={frameworkcontractData.id} title="Documenti correlati" />
									</div>
								),
								...(shouldShowChecklist ? { defaultIsVisible: false } : {}),
							},
							...(shouldShowChecklist
								? [
										{
											key: 'checklist',
											title: 'Checklist',
											content: (
												<div className="custom-checklist-container">
													<div>
														<h4>Checklist per il controllo</h4>

														<SummaryDownloadList
															entity={{
																entityType: 'frameworkcontracts',
																entityId: frameworkcontractData.id,
																label: 'contratti aperti',
															}}
															hasStandardChecklist={frameworkcontractData.tender.companyId === 2}

															// entityType="frameworkcontracts" entityId={frameworkcontractData.id} label="contratti aperti"
														/>
													</div>

													{frameworkcontractData.id && (
														<CheckList
															hasStandardChecklist={frameworkcontractData.tender.companyId === 2}
															entityType="frameworkcontracts"
															entityId={frameworkcontractData.id}
															disabled={isDisabled}
															hasInternalDownload={false}
															hasEnabledAi={project.aiEnabled}
														/>
													)}
												</div>
											),
										},
									]
								: []),
						]}
					/>
				) : null}
			</Modal.Body>
			<Modal.Footer>
				<Button onClick={onHide} className="btn custom-outline-button custom-medium-button">
					Annulla
				</Button>
				{shouldShowChecklist ? (
					<Button
						onClick={() => {
							setIsEdited(true);
						}}
						disabled={isDisabled}
						type="submit"
						form="checklist-frameworkcontracts"
						className="btn custom-primary-button custom-medium-button"
					>
						Salva risposte
					</Button>
				) : null}
			</Modal.Footer>
		</Modal>
	);
};

export default SummaryFrameworkcontractModal;
